export const pages = {
  fr: {
    common: {
      back: "Retour à l'accueil",
      more: "En savoir plus",
      ctaTitle: "Un projet ou un programme à piloter ?",
      ctaText: "Échangeons sur votre contexte, vos contraintes et les prochaines étapes.",
      related: "Pages associées",
    },
    gestionDeProjets: {
      path: "/gestion-de-projets",
      overline: "Gestion de projets",
      h1: "Gestion de projets & programmes industriels",
      intro:
        "MKN Projects renforce ou prend en charge le pilotage de projets et programmes complexes dans des environnements industriels et technologiques exigeants. Une pratique certifiée PMP®, construite sur plus de dix années d'expérience.",
      situationsTitle: "Quand faire appel à MKN Projects",
      situations: [
        {
          name: "Un projet à cadrer ou à remettre sur les rails",
          text: "Clarifier les objectifs, le périmètre et la gouvernance, puis établir un référentiel partagé : planning, budget, exigences et organisation.",
        },
        {
          name: "Un renfort temporaire de pilotage",
          text: "Absorber un pic d'activité, assurer une transition ou compléter une équipe projet sans alourdir durablement la structure.",
        },
        {
          name: "Un programme à multiples interfaces",
          text: "Coordonner fournisseurs, partenaires et métiers internes lorsque les dépendances techniques et organisationnelles se multiplient.",
        },
        {
          name: "Un besoin de visibilité pour décider",
          text: "Mettre en place des indicateurs, un reporting fiable et des revues régulières pour éclairer les arbitrages de la direction.",
        },
      ],
      scopeTitle: "Périmètre d'intervention",
      scope: [
        {
          name: "Cadrage & gouvernance",
          items: [
            "Définition des objectifs, du périmètre et des livrables",
            "Organisation projet, rôles et instances de décision",
            "Planning de référence et jalons clés",
            "Identification initiale des risques et des interfaces",
          ],
        },
        {
          name: "Pilotage de l'exécution",
          items: [
            "Suivi des coûts, de la qualité et des délais",
            "Gestion des risques et des opportunités",
            "Coordination des équipes multidisciplinaires",
            "Maîtrise des évolutions de périmètre",
          ],
        },
        {
          name: "Interfaces, fournisseurs & reporting",
          items: [
            "Gestion des interfaces techniques et organisationnelles",
            "Pilotage des fournisseurs et partenaires",
            "Reporting, indicateurs et aide à la décision",
            "Accompagnement jusqu'à la livraison et au transfert",
          ],
        },
      ],
      deliverablesTitle: "Livrables types",
      deliverablesText:
        "Les livrables sont adaptés à la maturité de l'organisation et à la taille du projet. Ils constituent un socle commun pour piloter et décider.",
      deliverables: [
        "Plan de management de projet",
        "Planning de référence et suivi des jalons",
        "Registre des risques et opportunités",
        "Matrice des interfaces et des dépendances",
        "Tableau de bord coûts · qualité · délais",
        "Revues de projet et relevés de décisions",
      ],
      principlesTitle: "Principes de pilotage",
      principles: [
        { name: "Gouvernance claire", text: "Des rôles, des instances et des règles de décision explicites dès le cadrage." },
        { name: "Référentiels partagés", text: "Un planning, un budget et des exigences compris et acceptés par l'ensemble des parties." },
        { name: "Risques et interfaces", text: "Une anticipation active des dépendances plutôt qu'une gestion de crise." },
        { name: "Orientation résultat", text: "Un pilotage tourné vers la livraison et la mise en service, pas vers le reporting pour lui-même." },
      ],
      certification: "Expertise en gestion de projets certifiée Project Management Professional (PMP®).",
      sectorsTitle: "Environnements d'application",
      sectors: [
        { label: "Aéronautique & Défense", href: "/aeronautique-defense", text: "Secteur historique : systèmes embarqués, avionique, intégration." },
        { label: "Industrie & Technologies", href: "/#industrie-technologies", text: "Systèmes connectés, IoT industriel, projets technologiques." },
        { label: "Énergie & électrification", href: "/#famille-1", text: "Structuration et pilotage de projets énergétiques." },
      ],
      related: [
        { label: "Développement commercial", href: "/#developpement-commercial" },
        { label: "Modes d'intervention", href: "/#modes" },
      ],
    },
    aeronautiqueDefense: {
      path: "/aeronautique-defense",
      overline: "Secteur historique",
      h1: "Gestion de projets aéronautique & défense",
      intro:
        "Plus de dix années de gestion de projets sur des systèmes embarqués, des équipements avioniques et des systèmes de communication pour plateformes aériennes, habitées et non habitées.",
      contextTitle: "Un secteur aux exigences spécifiques",
      contextText:
        "Les projets aéronautiques et de défense se caractérisent par des cycles longs, des processus de qualification structurants, une forte imbrication entre logiciel, électronique et équipements, et un grand nombre d'acteurs. Le pilotage doit concilier rigueur documentaire, maîtrise des interfaces et tenue des jalons.",
      constraints: [
        { name: "Exigences & qualification", text: "Des exigences formalisées et des étapes de qualification qui structurent le planning et les livrables." },
        { name: "Intégration & interfaces", text: "Des systèmes dont la valeur dépend de la cohérence entre logiciel, matériel et plateforme hôte." },
        { name: "Multi-acteurs", text: "Bureaux d'études, fournisseurs, intégrateurs et parties prenantes à coordonner sur toute la durée du projet." },
        { name: "Confidentialité", text: "Un cadre où la discrétion et le respect des règles de protection de l'information sont la norme." },
      ],
      domainsTitle: "Domaines d'intervention",
      domains: [
        { name: "Systèmes embarqués", text: "Pilotage du développement et de l'évolution de systèmes embarqués sur plateformes aériennes." },
        { name: "Équipements avioniques", text: "Coordination des activités de conception, de développement et de qualification d'équipements." },
        { name: "Systèmes de communication pour aéronefs", text: "Suivi de projets associant radio, protocoles, logiciel et contraintes d'intégration." },
        { name: "Intégration de systèmes", text: "Gestion des interfaces et des campagnes d'intégration entre sous-ensembles et plateforme." },
        { name: "Logiciels pour plateformes non habitées", text: "Pilotage de développements logiciels pour drones et systèmes aériens non habités." },
        { name: "Coordination logiciel / matériel", text: "Synchronisation des cycles de développement et des jalons entre équipes logicielles et matérielles." },
        { name: "Programmes complexes", text: "Gestion de programmes multi-projets avec dépendances, fournisseurs et jalons contractuels." },
      ],
      missionsTitle: "Nature des missions",
      missions: [
        "Cadrage et planification de projets de développement ou d'évolution de systèmes",
        "Pilotage coûts, qualité, délais et suivi des jalons de qualification",
        "Coordination des équipes logicielles, matérielles et d'intégration",
        "Gestion des interfaces avec fournisseurs, intégrateurs et parties prenantes",
        "Gestion des risques, des évolutions et des aléas techniques",
        "Reporting et préparation des revues de projet",
      ],
      confidentialityTitle: "Confidentialité",
      confidentialityText:
        "MKN Projects ne communique aucune information sur ses clients, employeurs, programmes ou systèmes. Les missions sont conduites dans le respect des exigences de confidentialité propres au secteur.",
      transferTitle: "Une expertise transposable",
      transferText:
        "La rigueur acquise dans l'aéronautique et la défense — gestion des exigences, des interfaces et des jalons — est aujourd'hui mobilisée sur des projets industriels, technologiques et énergétiques.",
      related: [
        { label: "Gestion de projets & programmes", href: "/gestion-de-projets" },
        { label: "Industrie & Technologies", href: "/#industrie-technologies" },
      ],
    },
  },
  en: {
    common: {
      back: "Back to home",
      more: "Learn more",
      ctaTitle: "A project or programme to steer?",
      ctaText: "Let's discuss your context, your constraints and the next steps.",
      related: "Related pages",
    },
    gestionDeProjets: {
      path: "/gestion-de-projets",
      overline: "Project Management",
      h1: "Industrial project & programme management",
      intro:
        "MKN Projects reinforces or takes charge of the management of complex projects and programmes in demanding industrial and technological environments. A PMP®-certified practice built on more than ten years of experience.",
      situationsTitle: "When to call on MKN Projects",
      situations: [
        {
          name: "A project to frame or put back on track",
          text: "Clarify objectives, scope and governance, then establish a shared baseline: schedule, budget, requirements and organisation.",
        },
        {
          name: "Temporary management reinforcement",
          text: "Absorb a peak of activity, manage a transition or complete a project team without permanently expanding the organisation.",
        },
        {
          name: "A programme with multiple interfaces",
          text: "Coordinate suppliers, partners and internal disciplines when technical and organisational dependencies multiply.",
        },
        {
          name: "Visibility to make decisions",
          text: "Set up indicators, reliable reporting and regular reviews to inform management decisions.",
        },
      ],
      scopeTitle: "Scope of intervention",
      scope: [
        {
          name: "Framing & governance",
          items: [
            "Definition of objectives, scope and deliverables",
            "Project organisation, roles and decision bodies",
            "Baseline schedule and key milestones",
            "Initial identification of risks and interfaces",
          ],
        },
        {
          name: "Execution management",
          items: [
            "Cost, quality and schedule monitoring",
            "Risk and opportunity management",
            "Coordination of multidisciplinary teams",
            "Scope change control",
          ],
        },
        {
          name: "Interfaces, suppliers & reporting",
          items: [
            "Technical and organisational interface management",
            "Supplier and partner management",
            "Reporting, indicators and decision support",
            "Support through to delivery and handover",
          ],
        },
      ],
      deliverablesTitle: "Typical deliverables",
      deliverablesText:
        "Deliverables are adapted to the organisation's maturity and the size of the project. They form a common foundation for steering and decision-making.",
      deliverables: [
        "Project management plan",
        "Baseline schedule and milestone tracking",
        "Risk and opportunity register",
        "Interface and dependency matrix",
        "Cost · quality · schedule dashboard",
        "Project reviews and decision records",
      ],
      principlesTitle: "Management principles",
      principles: [
        { name: "Clear governance", text: "Explicit roles, bodies and decision rules from the framing stage." },
        { name: "Shared baselines", text: "A schedule, a budget and requirements understood and accepted by all parties." },
        { name: "Risks and interfaces", text: "Active anticipation of dependencies rather than crisis management." },
        { name: "Results-oriented", text: "Management focused on delivery and commissioning, not on reporting for its own sake." },
      ],
      certification: "Project management expertise certified Project Management Professional (PMP®).",
      sectorsTitle: "Application environments",
      sectors: [
        { label: "Aerospace & Defence", href: "/aeronautique-defense", text: "Historical sector: embedded systems, avionics, integration." },
        { label: "Industry & Technologies", href: "/#industrie-technologies", text: "Connected systems, industrial IoT, technology projects." },
        { label: "Energy & electrification", href: "/#famille-1", text: "Structuring and management of energy projects." },
      ],
      related: [
        { label: "Business Development", href: "/#developpement-commercial" },
        { label: "Engagement models", href: "/#modes" },
      ],
    },
    aeronautiqueDefense: {
      path: "/aeronautique-defense",
      overline: "Historical sector",
      h1: "Aerospace & defence project management",
      intro:
        "More than ten years of project management on embedded systems, avionics equipment and communication systems for manned and unmanned aerial platforms.",
      contextTitle: "A sector with specific requirements",
      contextText:
        "Aerospace and defence projects are characterised by long cycles, structuring qualification processes, a strong interweaving of software, electronics and equipment, and a large number of stakeholders. Management must reconcile documentary rigour, interface control and milestone adherence.",
      constraints: [
        { name: "Requirements & qualification", text: "Formalised requirements and qualification stages that structure the schedule and deliverables." },
        { name: "Integration & interfaces", text: "Systems whose value depends on the consistency between software, hardware and host platform." },
        { name: "Multiple stakeholders", text: "Engineering offices, suppliers, integrators and stakeholders to coordinate throughout the project." },
        { name: "Confidentiality", text: "An environment where discretion and compliance with information protection rules are the norm." },
      ],
      domainsTitle: "Areas of intervention",
      domains: [
        { name: "Embedded systems", text: "Management of the development and evolution of systems embedded on aerial platforms." },
        { name: "Avionics equipment", text: "Coordination of equipment design, development and qualification activities." },
        { name: "Aircraft communication systems", text: "Monitoring of projects combining radio, protocols, software and integration constraints." },
        { name: "Systems integration", text: "Management of interfaces and integration campaigns between sub-assemblies and platform." },
        { name: "Software for unmanned platforms", text: "Management of software developments for drones and unmanned aerial systems." },
        { name: "Software / hardware coordination", text: "Synchronisation of development cycles and milestones between software and hardware teams." },
        { name: "Complex programmes", text: "Management of multi-project programmes with dependencies, suppliers and contractual milestones." },
      ],
      missionsTitle: "Nature of assignments",
      missions: [
        "Framing and planning of system development or evolution projects",
        "Cost, quality and schedule control and qualification milestone tracking",
        "Coordination of software, hardware and integration teams",
        "Interface management with suppliers, integrators and stakeholders",
        "Management of risks, changes and technical contingencies",
        "Reporting and preparation of project reviews",
      ],
      confidentialityTitle: "Confidentiality",
      confidentialityText:
        "MKN Projects does not disclose any information about its clients, employers, programmes or systems. Assignments are conducted in compliance with the sector's confidentiality requirements.",
      transferTitle: "Transferable expertise",
      transferText:
        "The rigour acquired in aerospace and defence — requirements, interface and milestone management — is now applied to industrial, technological and energy projects.",
      related: [
        { label: "Project & programme management", href: "/gestion-de-projets" },
        { label: "Industry & Technologies", href: "/#industrie-technologies" },
      ],
    },
  },
};
